import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { CHAT_STATUS_GENERAL, ROUTE_NAMES } from "@root/constants";
import { useRouteDetection } from "./useRouteDetection";

interface UsePlaceholderTextProps {
  currentMessageStatus?: string | number;
  selectedProject?: any;
}

/**
 * Custom hook to determine placeholder text based on current route and context
 */
export const usePlaceholderText = ({
  currentMessageStatus,
  selectedProject,
}: UsePlaceholderTextProps) => {
  const { t } = useTranslation("common");
  const { pathname, isMultiDoc, isLegalChatbot, isProjectDocument, isKcs, isDrawingAnalysis } =
    useRouteDetection();

  const placeholderText = useMemo(() => {
    if (currentMessageStatus === CHAT_STATUS_GENERAL.STREAMING)
      return t("translate.responding");
    if (isMultiDoc) return t("translate.uploadFiles");
    if (isLegalChatbot) return t("translate.placeholderLegal");
    if (isProjectDocument) {
      if (selectedProject) {
        return t("translate.placeholderProject");
      }
      return t("translate.placeholderAllProjects");
    }
    if (isKcs) return t("translate.askKcs");
    if (pathname === ROUTE_NAMES.DRAWING_ANALYSIS_DASHBOARD) {
      return t("translate.placeholderDrawing");
    }
    if (pathname.startsWith(ROUTE_NAMES.DRAWING_ANALYSIS_CHAT)) {
      return t("translate.placeholderDrawingAnalysis");
    }
    return t("translate.placeholderP1");
  }, [
    currentMessageStatus,
    isMultiDoc,
    isLegalChatbot,
    isProjectDocument,
    isKcs,
    isDrawingAnalysis,
    t,
    selectedProject,
    pathname,
  ]);

  return placeholderText;
};
